import { useEffect, useMemo, useState } from "react";
import { useQuery, type QueryKey } from "@tanstack/react-query";

import { ViewerDialog } from "@/shared/components/viewer";

/* An image stored as a chat file, drawn small where it was said: its small version (a
   WebP the server makes beside a made image), or the image itself when it has none. A click
   opens the full image in the viewer, which downloads it. The bytes need the bearer, so
   each is read through `read` (the platform's /attachments or the client app's files) and
   shown from a blob URL that lives as long as the component. */

export interface StoredImage {
  file: string;
  thumbnail?: string;
  name: string;
}

/** The size an image holds while its bytes come, and when they don't. */
export const IMAGE_BOX =
  "w-[340px] max-w-full aspect-square rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface-sunk)]";

type Read = { read: (id: string) => Promise<Blob>; keyOf: (id: string) => QueryKey };

function useBlob(id: string, { read, keyOf }: Read, enabled = true) {
  // a file's bytes never change
  return useQuery({ queryKey: keyOf(id), queryFn: () => read(id), staleTime: Infinity, enabled });
}

function useBlobUrl(blob: Blob | undefined): string | null {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

export default function ImageFile({ image, alt, ...reader }: { image: StoredImage; alt: string } & Read) {
  const [open, setOpen] = useState(false);
  const small = useBlob(image.thumbnail ?? image.file, reader);
  const full = useBlob(image.file, reader, open);
  const url = useBlobUrl(small.data);
  const original = useMemo(
    () => (full.data ? new File([full.data], image.name, { type: full.data.type }) : undefined),
    [full.data, image.name],
  );
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open ${image.name}`}
        aria-busy={open && full.isPending}
        className="block min-w-0 max-w-full rounded-[12px] cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {url ? (
          <img
            src={url}
            alt={alt}
            className="rounded-[12px] border border-[var(--hairline)] max-w-full max-h-[340px] object-cover shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
          />
        ) : (
          <div className={`${IMAGE_BOX} flex items-center justify-center px-4 text-center text-[12.5px] text-[var(--text-tertiary)]`}>
            {small.isError && `Couldn't load ${image.name}`}
          </div>
        )}
      </button>
      {open && full.isError && (
        <p role="alert" className="text-[12px] text-[var(--text-tertiary)]">Couldn't open {image.name}</p>
      )}
      {open && original && (
        <ViewerDialog mode="file" title={image.name} file={{ name: image.name, file: original }} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
